import dayjs from 'dayjs';
import { ComponentList } from '../../components/core/component-list.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { QueryableOptionalString } from '../core/optional.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { cloneListOfCloneable, DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { CustomerProfile } from './customer-profile.js';
import { DayOfYear } from './day-of-year.js';
import { CalendarOrderItem } from './calendar-order-item.js';
import { StaffProfile } from './staff-profile.js';
import { TimeOfDay } from './time-of-day.js';
import { TimeZoneRegion } from './time-zone-region.js';

export class CalendarLock extends DataModel {
  static readonly TYPE_ID: string = 'b55feec0cf4f4e53a0670d639b9629c6';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly dayId: string,
    public readonly dayOfYear: DayOfYear,
    public readonly timeOfDay: TimeOfDay,
    public readonly timezone: TimeZoneRegion,
    public readonly creationDate: dayjs.Dayjs,
    public readonly staffMemberId: string,
    public readonly staffMember: StaffProfile,
    public readonly expirationDate: dayjs.Dayjs,
    public readonly client: CustomerProfile | null,
    public readonly clientId: QueryableOptionalString,
    public readonly services: CalendarOrderItem[],
    public readonly components: ComponentList,
  ) {
    super();
  }

  get $typeId(): string {
    return CalendarLock.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CalendarLock.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData) {
    return new CalendarLock(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'dayId'),
        context.fromData<DayOfYear>(MapUtils.get<SerializedData>(map, 'dayOfYear')),
        context.fromData<TimeOfDay>(MapUtils.get<SerializedData>(map, 'timeOfDay')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'staffMemberId'),
        context.fromData<StaffProfile>(MapUtils.get<SerializedData>(map, 'staffMember')),
        context.decodeDate(MapUtils.get<number>(map, 'expirationDate')),
        context.fromData<CustomerProfile>(MapUtils.get<SerializedData>(map, 'client')),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'clientId')),
        MapUtils.getList<CalendarOrderItem>(
            map,
            'services',
            (data: unknown) => context.fromData<CalendarOrderItem>(data as SerializedData),
        ),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'businessRef': this.businessRef.encode(),
      'dayId': this.dayId,
      'dayOfYear': context.toData(this.dayOfYear),
      'timeOfDay': context.toData(this.timeOfDay),
      'timezone': fromEnumAsString(this.timezone),
      'creationDate': context.encodeDate(this.creationDate),
      'staffMemberId': this.staffMemberId,
      'staffMember': context.toData(this.staffMember),
      'expirationDate': context.encodeDate(this.expirationDate),
      'client': context.toDataOrNull(this.client),
      'clientId': this.clientId.value,
      'services': this.services.map((e) => context.toData(e)),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new CalendarLock(
        this.uniqueId,
        this.creatorId,
        this.businessRef.clone(),
        this.dayId,
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.staffMemberId,
        this.staffMember.clone(),
        TimeZone.cloneDate(this.expirationDate),
        this.client?.clone() ?? null,
        this.clientId.clone(),
        cloneListOfCloneable(this.services),
        this.components.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        isSameData(other.businessRef, this.businessRef) &&
        other.dayId === this.dayId &&
        isSameData(other.dayOfYear, this.dayOfYear) &&
        isSameData(other.timeOfDay, this.timeOfDay) &&
        other.timezone === this.timezone &&
        other.creationDate === this.creationDate &&
        other.staffMemberId === this.staffMemberId &&
        isSameData(other.staffMember, this.staffMember) &&
        other.expirationDate === this.expirationDate &&
        isSameData(other.client, this.client) &&
        isSameData(other.clientId, this.clientId) &&
        isSameList(other.services, this.services, isSameData) &&
        isSameData(other.components, this.components);
  }
}

export class CalendarLockBuilder extends DataModelBuilder<CalendarLock> {
  build(context: SerializationContext, map: SerializedData): CalendarLock {
    return CalendarLock.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CalendarLock.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CalendarLock.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [businessRef] was replaced with [businessRef]
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
